"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    MILLESIME,
    SITUATIONS,
    champsDuFormulaire,
    concerne,
    recapitulatif,
    valider,
} from "@/lib/tools/acreRequest";
import { telechargerFormulaire } from "@/lib/tools/officialFormPdf";
import { dateEnToutesLettres, nomDuFichier } from "@/lib/tools/documentText";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Question,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La demande d'ACRE, sur l'imprimé de l'Urssaf.
 *
 * TROIS BARRIÈRES AVANT LA PREMIÈRE CASE, parce que ce formulaire ne vaut
 * qu'à trois conditions que rien sur l'imprimé ne rappelle assez fort : la
 * demande se transmet dans les 60 jours du début d'activité déclaré au guichet
 * unique, une seule des onze situations suffit mais il en faut une, et
 * l'attestation sur l'honneur engage sur l'absence d'ACRE dans les trois ans.
 * Qui bute sur l'une des trois repart avec la raison, pas avec un PDF.
 *
 * ICI LE PAVÉ DE SIGNATURE EST CELUI DE LA VISITE : c'est le demandeur qui
 * signe, donc l'utilisateur. Le paraphe se dessine dans la zone que l'imprimé
 * réserve, à une taille de signature.
 *
 * Le numéro de sécurité sociale est la donnée la plus sensible de la rubrique.
 * Même règle que le reste : composé dans le navigateur, jamais envoyé, jamais
 * conservé, et le premier écran le dit.
 */

const L = {
    fr: {
        phases: ["Votre droit", "Vous", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil, numéro de sécurité sociale compris : l'imprimé s'y remplit, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        declareTitre: "Avez-vous déjà déclaré votre activité au guichet unique ?",
        declareAide: "La demande d'ACRE se transmet après la déclaration, avec le justificatif de création que le guichet vous remet. Pas avant.",
        declareOui: "Oui, c'est fait",
        declareOuiDetail: "J'ai mon justificatif de création",
        declareNon: "Pas encore",
        declareNonDetail: "Je prépare ma déclaration",
        dateTitre: "Quelle date de début d'activité avez-vous déclarée ?",
        dateAide: "Celle du guichet unique. Le délai de 60 jours court à partir d'elle, pas de l'arrivée de votre attestation d'affiliation.",
        champDateDebut: "Date de début d'activité",
        delaiRestant: "Il vous reste {jours} jours pour transmettre la demande, jusqu'au {limite}.",
        dejaTitre: "Avez-vous bénéficié de l'ACRE au cours des trois dernières années ?",
        dejaAide: "L'imprimé vous le fait attester sur l'honneur. Les trois ans se comptent depuis la fin du bénéfice précédent.",
        dejaNon: "Non, jamais",
        dejaNonDetail: "Ou il y a plus de trois ans",
        dejaOui: "Oui",
        dejaOuiDetail: "Une précédente création, il y a moins de trois ans",
        cessationTitre: "Aviez-vous une activité indépendante que vous avez cessée ?",
        cessationAide:
            "Cette règle est distincte de la précédente, et elle vaut même sans ACRE antérieure : une reprise intervenue dans l'année de la cessation ou dans l'année suivante n'est pas un début d'activité, et l'ACRE n'exonère qu'un début d'activité.",
        cessationNon: "Non, c'est ma première",
        cessationNonDetail: "Ou la précédente s'est arrêtée il y a longtemps",
        cessationOui: "Oui, j'en ai cessé une",
        cessationOuiDetail: "Quelle qu'elle ait été, et même si elle n'a rien encaissé",
        cessationDateTitre: "Quand cette activité a-t-elle cessé ?",
        cessationDateAide:
            "Seule l'année compte : celle de la cessation et celle qui la suit sont fermées, quel que soit le jour.",
        champDateCessation: "Date de cessation",
        motifReprisePrecoce:
            "L'article R. 131-3 du code de la sécurité sociale écarte du début d'activité toute reprise intervenue dans l'année de la cessation ou dans l'année suivante. L'ACRE n'exonérant qu'un début d'activité, la demande serait rejetée, et l'attestation sur l'honneur de l'imprimé ne porte pas sur ce point : c'est l'Urssaf qui le vérifie.",

        situationTitre: "Dans quelle situation êtes-vous ?",
        situationAide: "Une seule suffit, et chacune exige sa pièce justificative, indiquée sous le titre. Choisissez celle que vous pouvez prouver.",
        sit1: "Demandeur d'emploi indemnisé",
        sit1Detail: "Notification de droit ou dernier paiement",
        sit2: "Demandeur d'emploi non indemnisé",
        sit2Detail: "Historique de l'inscription à France Travail",
        sit3: "Bénéficiaire du RSA ou de l'ASS",
        sit3Detail: "Attestation d'allocataire",
        sit4: "Entre 18 et 25 ans",
        sit4Detail: "Pièce d'identité",
        sit5: "Moins de 30 ans, non indemnisé",
        sit5Detail: "Pièce d'identité et attestation sur l'honneur",
        sit6: "Moins de 30 ans, reconnu handicapé",
        sit6Detail: "Pièce d'identité et justificatif de la CDAPH",
        sit7: "Salarié ou licencié qui reprend son entreprise",
        sit7Detail: "Jugement d'ouverture ou attestation",
        sit8: "Contrat d'appui au projet d'entreprise",
        sit8Detail: "Copie du Cape",
        sit9: "Création en quartier prioritaire",
        sit9Detail: "Justificatif d'adresse dans le QPV",
        sit10: "Bénéficiaire de la PreParE",
        sit10Detail: "Notification ou dernier paiement",
        sit11: "Activité en zone France ruralités revitalisation",
        sit11Detail: "Justificatif d'adresse en ZFRR ou ZFRR+",
        sitAucune: "Aucune ne me correspond",
        sitAucuneDetail: "Ou je ne sais pas",
        horsSujetTitre: "La demande ne peut pas partir",
        motifPasDeclare: "La demande d'ACRE se transmet une fois l'activité déclarée au guichet unique, avec le justificatif de création qu'il vous remet à la fin de la formalité. Revenez à ce moment-là : vous aurez 60 jours, et l'imprimé se remplit en cinq minutes.",
        motifHorsDelai: "Le délai de 60 jours à compter de votre début d'activité déclaré est dépassé depuis le {limite}. Depuis le 1er janvier 2026, passé ce délai, l'aide est perdue, et une demande tardive est refusée. Il n'y a pas de rattrapage, sauf nouvelle création dans les conditions prévues.",
        motifDejaBeneficie: "L'imprimé vous fait attester sur l'honneur que l'ACRE ne vous a pas été accordée au cours des trois dernières années. Si c'est le cas, vous ne pouvez pas le signer, et l'Urssaf le vérifiera de toute façon dans vos droits.",
        motifAucuneSituation: "L'ACRE n'est ouverte qu'aux personnes dans l'une des onze situations du formulaire, chacune avec sa pièce justificative. Sans l'une d'elles, la demande sera refusée. Si vous hésitez, le simulateur d'ACRE reprend les conditions une à une avec vous.",
        versSimulateur: "Revoir les conditions dans le simulateur d'ACRE",
        identiteTitre: "Qui êtes-vous ?",
        identiteAide: "L'orthographe exacte de votre pièce d'identité, que l'Urssaf rapprochera de votre déclaration au guichet unique.",
        madame: "Madame",
        monsieur: "Monsieur",
        champNomNaissance: "Nom de naissance",
        champNomUsage: "Nom d'usage",
        aideNomUsage: "Facultatif. Le nom que vous portez s'il diffère, nom d'épouse par exemple.",
        champPrenoms: "Prénoms",
        champNationalite: "Nationalité",
        champDateNaissance: "Date de naissance",
        contactTitre: "Votre numéro de sécurité sociale et vos coordonnées",
        contactAide: "L'Urssaf répond par la messagerie de votre espace en ligne et par courriel. Le numéro est contrôlé ici avant de partir : un numéro faux fait rejeter la demande, souvent après les 60 jours.",
        champNni: "Numéro de sécurité sociale",
        aideNni: "Treize chiffres, ou quinze avec la clé. Il commence par 1 pour un homme, 2 pour une femme.",
        champMail: "Adresse électronique",
        champTelephone: "Téléphone",
        aideTelephone: "Facultatif.",
        domicileTitre: "Où habitez-vous ?",
        domicileAide: "Votre domicile personnel. Les deux cases du bas ne concernent que des situations rares.",
        champAdresse: "Adresse",
        champCp: "Code postal",
        champVille: "Ville",
        champPays: "Pays",
        ambulant: "Je n'ai pas de domicile stable (activité ambulante)",
        champCommuneActivite: "Commune de l'activité principale",
        champCpActivite: "Code postal de cette commune",
        mineur: "Je suis mineur émancipé, autorisé à être commerçant par décision judiciaire",
        signatureTitre: "L'attestation sur l'honneur, et votre signature",
        signatureAide: "En signant, vous attestez que l'ACRE ne vous a pas été accordée au cours des trois dernières années et que les renseignements sont exacts, sous peine des sanctions prévues par la loi.",
        champDateSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",
        continuer: "Continuer",
        voirDocument: "Voir ce qui sera déclaré",
        resultatTitre: "Votre demande d'ACRE",
        recapTitre: "Ce qui partira dans l'imprimé",
        recapAide: "Relisez avant de télécharger : le formulaire de l'Urssaf s'ouvrira rempli de ces valeurs, et vous pourrez encore le corriger dans votre lecteur de PDF.",
        piecesTitre: "Les deux pièces à joindre",
        telecharger: "Télécharger l'imprimé rempli",
        recommencer: "Recommencer",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre imprimé.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
        erreurDateLue: "Nous lisons {date}. Vérifiez l'année.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurNniFormat: "Treize ou quinze chiffres, commençant par 1 ou 2. Vous en avez saisi {compte}.",
        erreurNniCle: "La clé de contrôle ne correspond pas aux treize premiers chiffres : un chiffre a dû glisser.",
        alerteNirEtSexe: "Votre numéro de sécurité sociale commence par un chiffre qui ne correspond pas à la civilité choisie. Vérifiez l'un ou l'autre avant d'envoyer.",
        alerteAgeJeune: "La situation « jeune de 18 à 25 ans révolus » ne correspond pas à votre date de naissance. L'Urssaf la vérifiera sur votre pièce d'identité.",
        alerteAgeTrente: "La situation choisie exige d'avoir moins de 30 ans, et votre date de naissance dit le contraire. L'Urssaf la vérifiera sur votre pièce d'identité.",
        suiteTitre: "Et maintenant",
        suite1: "Envoyez l'imprimé et les deux pièces avant le {limite}, depuis la messagerie de votre espace sur autoentrepreneur.urssaf.fr.",
        suite2: "L'Urssaf répond dans le mois. Sans réponse passé ce délai, l'ACRE est présumée acceptée, et l'attestation apparaît dans « Mes attestations ».",
        suite3: "Gardez le PDF et l'accusé de la messagerie : en cas de contestation, ils prouvent la date d'envoi.",
        fondTitre: "Ce que vous demandez, en chiffres",
        fond: "Pour une activité démarrée à partir du 1er juillet 2026, l'ACRE réduit vos cotisations d'un quart : vous payez 75 % du taux normal, jusqu'à la fin du troisième trimestre civil qui suit votre début d'activité, soit neuf à douze mois selon la date. La période court même sans chiffre d'affaires, et l'aide est alors consommée pour trois ans.",
        millesimeNote: "Imprimé {reference}, tel que publié par l'Urssaf.",
    },
    en: {
        phases: ["Your entitlement", "You", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device, social security number included: the form is filled in there, nobody receives your answers, not even us, and it all disappears with the tab.",
        declareTitre: "Have you already declared your activity on the single window?",
        declareAide: "The ACRE application is sent after the declaration, with the proof of creation the single window gives you. Not before.",
        declareOui: "Yes, it is done",
        declareOuiDetail: "I have my proof of creation",
        declareNon: "Not yet",
        declareNonDetail: "I am preparing my declaration",
        dateTitre: "Which start date did you declare?",
        dateAide: "The one on the single window. The 60-day period runs from it, not from the arrival of your affiliation certificate.",
        champDateDebut: "Start date of activity",
        delaiRestant: "You have {jours} days left to send the application, until {limite}.",
        dejaTitre: "Have you received ACRE in the last three years?",
        dejaAide: "The form makes you swear to it. The three years count from the end of the previous period of relief.",
        dejaNon: "No, never",
        dejaNonDetail: "Or more than three years ago",
        dejaOui: "Yes",
        dejaOuiDetail: "A previous business, less than three years ago",
        cessationTitre: "Did you have a self-employed activity that you ceased?",
        cessationAide:
            "This rule is separate from the previous one, and it applies even without an earlier ACRE: a restart occurring in the year of the cessation or in the following year is not a start of activity, and ACRE only exempts a start of activity.",
        cessationNon: "No, this is my first",
        cessationNonDetail: "Or the previous one ended long ago",
        cessationOui: "Yes, I ceased one",
        cessationOuiDetail: "Whatever it was, and even if it collected nothing",
        cessationDateTitre: "When did that activity cease?",
        cessationDateAide:
            "Only the year matters: the year of the cessation and the one after it are closed, whatever the day.",
        champDateCessation: "Date of cessation",
        motifReprisePrecoce:
            "Article R. 131-3 of the social security code excludes from the start of an activity any restart occurring in the year of the cessation or in the following year. As ACRE only exempts a start of activity, the application would be rejected, and the form's sworn statement does not cover this point: the URSSAF checks it.",

        situationTitre: "Which situation are you in?",
        situationAide: "One is enough, and each requires its supporting document, shown under the title. Choose the one you can prove.",
        sit1: "Jobseeker receiving benefits",
        sit1Detail: "Notice of entitlement or last payment",
        sit2: "Jobseeker without benefits",
        sit2Detail: "France Travail registration history",
        sit3: "Receiving RSA or ASS",
        sit3Detail: "Beneficiary certificate",
        sit4: "Aged 18 to 25",
        sit4Detail: "Identity document",
        sit5: "Under 30, without benefits",
        sit5Detail: "Identity document and sworn statement",
        sit6: "Under 30, recognised disability",
        sit6Detail: "Identity document and CDAPH certificate",
        sit7: "Employee or former employee taking over the business",
        sit7Detail: "Court order or certificate",
        sit8: "Business project support contract",
        sit8Detail: "Copy of the Cape",
        sit9: "Setting up in a priority urban area",
        sit9Detail: "Proof of address in the QPV",
        sit10: "Receiving PreParE",
        sit10Detail: "Notice or last payment",
        sit11: "Activity in a rural revitalisation zone",
        sit11Detail: "Proof of address in a ZFRR or ZFRR+",
        sitAucune: "None applies to me",
        sitAucuneDetail: "Or I am not sure",
        horsSujetTitre: "The application cannot go",
        motifPasDeclare: "The ACRE application is sent once the activity is declared on the single window, with the proof of creation it gives you at the end of the formality. Come back then: you will have 60 days, and the form takes five minutes.",
        motifHorsDelai: "The 60-day period from your declared start date expired on {limite}. Since 1 January 2026, once it has passed the relief is lost, and a late application is refused. There is no catching up, short of a new business under the conditions provided.",
        motifDejaBeneficie: "The form makes you swear that ACRE was not granted to you in the last three years. If it was, you cannot sign it, and the URSSAF will check your record anyway.",
        motifAucuneSituation: "ACRE is only open to people in one of the form's eleven situations, each with its supporting document. Without one of them, the application will be refused. If you are hesitating, the ACRE calculator goes through the conditions with you one at a time.",
        versSimulateur: "Go over the conditions in the ACRE calculator",
        identiteTitre: "Who are you?",
        identiteAide: "The exact spelling of your identity document, which the URSSAF will match against your single-window declaration.",
        madame: "Ms",
        monsieur: "Mr",
        champNomNaissance: "Birth name",
        champNomUsage: "Name in use",
        aideNomUsage: "Optional. The name you go by if it differs, a married name for example.",
        champPrenoms: "Forenames",
        champNationalite: "Nationality",
        champDateNaissance: "Date of birth",
        contactTitre: "Your social security number and contact details",
        contactAide: "The URSSAF replies through your online account's messaging and by email. The number is checked here before it goes: a wrong number gets the application rejected, often after the 60 days.",
        champNni: "Social security number",
        aideNni: "Thirteen digits, or fifteen with the key. It starts with 1 for a man, 2 for a woman.",
        champMail: "Email address",
        champTelephone: "Telephone",
        aideTelephone: "Optional.",
        domicileTitre: "Where do you live?",
        domicileAide: "Your home address. The two boxes at the bottom only concern rare situations.",
        champAdresse: "Address",
        champCp: "Postcode",
        champVille: "Town",
        champPays: "Country",
        ambulant: "I have no fixed abode (itinerant activity)",
        champCommuneActivite: "Municipality of the main activity",
        champCpActivite: "Postcode of that municipality",
        mineur: "I am an emancipated minor, authorised to trade by court decision",
        signatureTitre: "The sworn statement, and your signature",
        signatureAide: "By signing, you swear that ACRE was not granted to you in the last three years and that the information is accurate, under the penalties provided by law.",
        champDateSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",
        continuer: "Continue",
        voirDocument: "See what will be declared",
        resultatTitre: "Your ACRE application",
        recapTitre: "What will go into the form",
        recapAide: "Read it before downloading: the URSSAF form will open filled with these values, and you can still correct it in your PDF reader.",
        piecesTitre: "The two documents to attach",
        telecharger: "Download the filled form",
        recommencer: "Start again",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your form.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but their content is not accepted. Correct them and come back.",
        erreurDateLue: "We read {date}. Please check the year.",
        erreurFormat: "This format is not recognised.",
        erreurNniFormat: "Thirteen or fifteen digits, starting with 1 or 2. You entered {compte}.",
        erreurNniCle: "The check key does not match the first thirteen digits: a digit must have slipped.",
        alerteNirEtSexe: "Your social security number starts with a digit that does not match the title you chose. Check one or the other before sending.",
        alerteAgeJeune: "The situation « aged 18 to 25 » does not match your date of birth. The URSSAF will check it against your identity document.",
        alerteAgeTrente: "The situation you chose requires being under 30, and your date of birth says otherwise. The URSSAF will check it against your identity document.",
        suiteTitre: "What happens next",
        suite1: "Send the form and the two documents before {limite}, from the messaging of your account on autoentrepreneur.urssaf.fr.",
        suite2: "The URSSAF replies within a month. With no reply after that, ACRE is deemed granted, and the certificate appears under « Mes attestations ».",
        suite3: "Keep the PDF and the messaging receipt: in case of dispute, they prove the date you sent it.",
        fondTitre: "What you are asking for, in figures",
        fond: "For an activity started on or after 1 July 2026, ACRE cuts your contributions by a quarter: you pay 75% of the normal rate, until the end of the third calendar quarter following your start, that is nine to twelve months depending on the date. The period runs even with no turnover, and the relief is then used up for three years.",
        millesimeNote: "Form {reference}, as published by the URSSAF.",
    },
};

const PHASES_PAR_ECRAN = {
    declare: 0,
    date: 0,
    deja: 0,
    situation: 0,
    horsSujet: 0,
    identite: 1,
    contact: 1,
    domicile: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    declare: "",
    dateDebut: "",
    dejaBeneficie: "",
    dateCessation: "",
    situation: "",
    sexe: "",
    nomNaissance: "",
    nomUsage: "",
    prenoms: "",
    nationalite: "Française",
    dateNaissance: "",
    nni: "",
    mail: "",
    telephone: "",
    adresse: "",
    cp: "",
    ville: "",
    pays: "France",
    ambulant: false,
    communeActivite: "",
    cpActivite: "",
    mineurEmancipe: false,
    dateSignature: "",
};

/** Une case à cocher avec son libellé. */
function Case({ libelle, coche, basculer }) {
    return (
        <label className="flex items-start gap-2.5 cursor-pointer">
            <input
                type="checkbox"
                checked={coche}
                onChange={(e) => basculer(e.target.checked)}
                className="mt-1 h-4 w-4 accent-primary cursor-pointer shrink-0"
            />
            <span className="text-sm text-gray-600 dark:text-lightBlue">{libelle}</span>
        </label>
    );
}

/** Les libellés des champs, hors du dictionnaire lu par le contrôle de parité. */
const LIBELLES_MANQUES = {
    fr: {
        declare: "Votre déclaration d'activité",
        situation: "Votre situation",
        sexe: "Votre civilité",
        nomNaissance: "Votre nom de naissance",
        prenoms: "Vos prénoms",
        nationalite: "Votre nationalité",
        dateNaissance: "Votre date de naissance",
        nni: "Votre numéro de sécurité sociale",
        mail: "Votre adresse électronique",
        adresse: "Votre adresse",
        cp: "Votre code postal",
        ville: "Votre ville",
        pays: "Votre pays",
        communeActivite: "La commune de votre activité principale",
        cpActivite: "Le code postal de cette commune",
        dateDebut: "Votre date de début d'activité",
        dateSignature: "La date de signature",
    },
    en: {
        declare: "Your activity declaration",
        situation: "Your situation",
        sexe: "Your title",
        nomNaissance: "Your birth name",
        prenoms: "Your forenames",
        nationalite: "Your nationality",
        dateNaissance: "Your date of birth",
        nni: "Your social security number",
        mail: "Your email address",
        adresse: "Your address",
        cp: "Your postcode",
        ville: "Your town",
        pays: "Your country",
        communeActivite: "The municipality of your main activity",
        cpActivite: "The postcode of that municipality",
        dateDebut: "Your start date",
        dateSignature: "The date of signature",
    },
};

export default function DemandeAcre({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    // L'identité gardée sur l'appareil préremplit les écrans « identité »,
    // « contact » et « domicile », que le lecteur relit : rien n'est sauté.
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [erreurTelechargement, setErreurTelechargement] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "declare",
        () => {
            // L'identité gardée revient : on recommence le document, pas sa saisie.
            setR({ ...VIDE, ...identite });
            setErreurTelechargement(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    /* L'identité se propose à la garde une fois le domicile passé. Le numéro
       de sécurité sociale n'en fait jamais partie : il n'est pas transmis. */
    useSignalerIdentite(
        {
            sexe: r.sexe,
            nomNaissance: r.nomNaissance,
            nomUsage: r.nomUsage,
            prenoms: r.prenoms,
            nationalite: r.nationalite,
            dateNaissance: r.dateNaissance,
            mail: r.mail,
            telephone: r.telephone,
            cp: r.cp,
            ville: r.ville,
        },
        etape === "signature" || etape === "resultat",
    );

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateSignature: r.dateSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const champs = useMemo(() => champsDuFormulaire(reponses, aujourdhui), [reponses, aujourdhui]);
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (champ === "nni" && refus.motif === "cle") return t.erreurNniCle;
        if (champ === "nni") {
            return remplir(t.erreurNniFormat, { compte: String(r.nni || "").replace(/\s/g, "").length });
        }
        if (refus.motif === "ageImpossible") {
            return remplir(t.erreurDateLue, { date: dateEnToutesLettres(r[champ]) });
        }
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r, aujourdhui);

    /* La date limite des 60 jours, lisible, pour l'écran de date et la suite. */
    const limite = useMemo(() => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(r.dateDebut)) return null;
        const d = new Date(`${r.dateDebut}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + MILLESIME.delaiJours);
        const jours = Math.ceil((d - new Date(aujourdhui)) / 86400000);
        return { texte: dateEnToutesLettres(d.toISOString().slice(0, 10)), jours };
    }, [r.dateDebut, aujourdhui]);

    const telecharger = async () => {
        try {
            setErreurTelechargement(false);
            await telechargerFormulaire({
                champs,
                signature,
                nomFichier: nomDuFichier("demande-acre", { prenoms: r.prenoms, nom: r.nomNaissance }),
            });
        } catch {
            setErreurTelechargement(true);
        }
    };

    const slugSimulateur = lang === "en" ? "acre-calculator" : "simulateur-acre";

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "declare") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.declareTitre} aide={t.declareAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="check-circle-linear"
                        titre={t.declareOui}
                        detail={t.declareOuiDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, declare: "oui" }));
                            aller("date");
                        }}
                    />
                    <Choix
                        icone="calendar-mark-linear"
                        titre={t.declareNon}
                        detail={t.declareNonDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, declare: "non" }));
                            aller("horsSujet");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "date") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.dateTitre} aide={t.dateAide}>
                <Champ
                    type="date"
                    libelle={t.champDateDebut}
                    valeur={r.dateDebut}
                    poser={poser("dateDebut")}
                    erreur={erreurDe("dateDebut")}
                />
                {limite && limite.jours >= 0 && (
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-3 mb-0">
                        {remplir(t.delaiRestant, { jours: limite.jours, limite: limite.texte })}
                    </p>
                )}
                <Avancer
                    pleineLargeur
                    disabled={!rempli("dateDebut")}
                    onClick={() => aller(tri.motif === "horsDelai" ? "horsSujet" : "deja")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "deja") {
        return (
            <Question
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.dejaTitre}
                aide={t.dejaAide}
                choix={[
                    {
                        icone: "check-circle-linear",
                        titre: t.dejaNon,
                        detail: t.dejaNonDetail,
                        onClick: () => {
                            setR((p) => ({ ...p, dejaBeneficie: "non" }));
                            aller("cessation");
                        },
                    },
                    {
                        icone: "calendar-linear",
                        titre: t.dejaOui,
                        detail: t.dejaOuiDetail,
                        onClick: () => {
                            setR((p) => ({ ...p, dejaBeneficie: "oui" }));
                            aller("horsSujet");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "cessation") {
        return (
            <Question
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.cessationTitre}
                aide={t.cessationAide}
                choix={[
                    {
                        icone: "check-circle-linear",
                        titre: t.cessationNon,
                        detail: t.cessationNonDetail,
                        onClick: () => {
                            setR((p) => ({ ...p, dateCessation: "" }));
                            aller("situation");
                        },
                    },
                    {
                        icone: "calendar-linear",
                        titre: t.cessationOui,
                        detail: t.cessationOuiDetail,
                        onClick: () => aller("dateCessation"),
                    },
                ]}
            />
        );
    }

    if (etape === "dateCessation") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.cessationDateTitre}
                aide={t.cessationDateAide}
            >
                <Champ
                    type="date"
                    libelle={t.champDateCessation}
                    valeur={r.dateCessation}
                    poser={poser("dateCessation")}
                />
                <Avancer
                    pleineLargeur
                    disabled={!rempli("dateCessation")}
                    onClick={() => aller(tri.motif === "reprisePrecoce" ? "horsSujet" : "situation")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "situation") {
        const cartes = SITUATIONS.map((s, i) => ({
            cle: s.cle,
            titre: t[`sit${i + 1}`],
            detail: t[`sit${i + 1}Detail`],
        }));
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.situationTitre} aide={t.situationAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {[...cartes, { cle: "aucune", titre: t.sitAucune, detail: t.sitAucuneDetail }].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.cle === "aucune" ? "info-circle-linear" : "clipboard-check-linear"}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, situation: c.cle }));
                                aller(c.cle === "aucune" ? "horsSujet" : "identite");
                            }}
                        />
                    ))}
                </div>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {tri.motif === "pasDeclare" && t.motifPasDeclare}
                        {tri.motif === "horsDelai" && remplir(t.motifHorsDelai, { limite: limite?.texte || "" })}
                        {tri.motif === "dejaBeneficie" && t.motifDejaBeneficie}
                        {tri.motif === "reprisePrecoce" && t.motifReprisePrecoce}
                        {tri.motif === "aucuneSituation" && t.motifAucuneSituation}
                    </p>
                    {tri.motif === "aucuneSituation" && (
                        <Link
                            href={{ pathname: "/tools/[slug]", params: { slug: slugSimulateur } }}
                            className="inline-block mt-5 text-sm font-semibold text-primary hover:!text-primary"
                        >
                            {t.versSimulateur} →
                        </Link>
                    )}
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "identite") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.identiteTitre} aide={t.identiteAide}>
                <div className="space-y-5">
                    <Segments
                        options={[
                            { cle: "F", libelle: t.madame },
                            { cle: "M", libelle: t.monsieur },
                        ]}
                        valeur={r.sexe}
                        poser={poser("sexe")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champNomNaissance} valeur={r.nomNaissance} poser={poser("nomNaissance")} />
                        <Champ libelle={t.champNomUsage} aide={t.aideNomUsage} valeur={r.nomUsage} poser={poser("nomUsage")} />
                    </div>
                    <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champNationalite} valeur={r.nationalite} poser={poser("nationalite")} />
                        <Champ
                            type="date"
                            libelle={t.champDateNaissance}
                            valeur={r.dateNaissance}
                            poser={poser("dateNaissance")}
                            erreur={erreurDe("dateNaissance")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("sexe", "nomNaissance", "prenoms", "nationalite", "dateNaissance") ||
                        Boolean(refusDe("dateNaissance"))
                    }
                    onClick={() => aller("contact")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "contact") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.contactTitre} aide={t.contactAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champNni}
                        aide={t.aideNni}
                        valeur={r.nni}
                        poser={poser("nni")}
                        erreur={erreurDe("nni")}
                        inputMode="numeric"
                    />
                    <Champ
                        type="email"
                        libelle={t.champMail}
                        valeur={r.mail}
                        poser={poser("mail")}
                        erreur={erreurDe("mail")}
                    />
                    <Champ
                        type="tel"
                        libelle={t.champTelephone}
                        aide={t.aideTelephone}
                        valeur={r.telephone}
                        poser={poser("telephone")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("nni", "mail") || Boolean(refusDe("nni")) || Boolean(refusDe("mail"))}
                    onClick={() => aller("domicile")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "domicile") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.domicileTitre} aide={t.domicileAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champAdresse} valeur={r.adresse} poser={poser("adresse")} />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ
                            libelle={t.champCp}
                            valeur={r.cp}
                            poser={poser("cp")}
                            erreur={erreurDe("cp")}
                            inputMode="numeric"
                        />
                        <div className="sm:col-span-2">
                            <Champ libelle={t.champVille} valeur={r.ville} poser={poser("ville")} />
                        </div>
                    </div>
                    <Champ libelle={t.champPays} valeur={r.pays} poser={poser("pays")} />
                    <div className="space-y-4 pt-1">
                        <Case libelle={t.ambulant} coche={r.ambulant} basculer={poser("ambulant")} />
                        {r.ambulant && (
                            <div className="grid sm:grid-cols-3 gap-4 pl-7">
                                <div className="sm:col-span-2">
                                    <Champ
                                        libelle={t.champCommuneActivite}
                                        valeur={r.communeActivite}
                                        poser={poser("communeActivite")}
                                    />
                                </div>
                                <Champ
                                    libelle={t.champCpActivite}
                                    valeur={r.cpActivite}
                                    poser={poser("cpActivite")}
                                    erreur={erreurDe("cpActivite")}
                                    inputMode="numeric"
                                />
                            </div>
                        )}
                        <Case libelle={t.mineur} coche={r.mineurEmancipe} basculer={poser("mineurEmancipe")} />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("adresse", "cp", "ville", "pays") ||
                        Boolean(refusDe("cp")) ||
                        (r.ambulant && (!rempli("communeActivite", "cpActivite") || Boolean(refusDe("cpActivite"))))
                    }
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateSignature || jour}
                        poser={poser("dateSignature")}
                        erreur={erreurDe("dateSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer pleineLargeur onClick={() => aller("resultat")}>
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!recap ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li key={`${champ}-${motif}`} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.length > 0 && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5 space-y-2">
                            {controle.alertes.includes("nirEtSexe") && (
                                <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{t.alerteNirEtSexe}</p>
                            )}
                            {controle.alertes.includes("ageJeune") && (
                                <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{t.alerteAgeJeune}</p>
                            )}
                            {controle.alertes.includes("ageTrente") && (
                                <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{t.alerteAgeTrente}</p>
                            )}
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-4">{t.recapAide}</p>

                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.lignes.map(([titre, valeur]) => (
                                    <tr key={titre} className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10">
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">{titre}</td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6">
                        <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                            {t.piecesTitre}
                        </div>
                        <ul className="space-y-2">
                            {recap.pieces.map((p) => (
                                <li key={p} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button
                        type="button"
                        onClick={telecharger}
                        className="w-full mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                    >
                        <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                        {t.telecharger}
                    </button>
                    {erreurTelechargement && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 mb-0">{t.erreurFormat}</p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">
                        {remplir(t.millesimeNote, { reference: MILLESIME.reference })}
                    </p>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon name="clipboard-check-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {[remplir(t.suite1, { limite: limite?.texte || "" }), t.suite2, t.suite3].map((ligne) => (
                                <li key={ligne} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon name="shield-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
